(() => {
  const sections = document.querySelectorAll('.acci-shop');
  const LENIS_VERSION = '1.3.23';

  // Mobile card gallery: non-primary slides render with data-src only, so nothing
  // loads until the user swipes (or hovers) that specific gallery.
  const hydrateCardGallery = (wrap) => {
    if (!wrap || wrap.dataset.galleryHydrated) return;
    wrap.dataset.galleryHydrated = 'true';
    wrap.querySelectorAll('img[data-src]').forEach((img) => {
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    });
    wrap.querySelectorAll('video.acci-product-card__media--video').forEach((video) => {
      if (video.dataset.poster) {
        video.setAttribute('poster', video.dataset.poster);
        video.removeAttribute('data-poster');
      }
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.setAttribute('src', source.dataset.src);
        source.removeAttribute('data-src');
      });
      video.preload = 'auto';
      video.load();
    });
    wrap.querySelectorAll('iframe.acci-product-card__media--video[data-src]').forEach((iframe) => {
      iframe.setAttribute('src', iframe.dataset.src);
      iframe.removeAttribute('data-src');
    });
  };

  const updateCardPagination = (wrap) => {
    const pagination = wrap?.closest('.acci-product-card')?.querySelector('.acci-product-card__pagination');
    if (!pagination) return;
    const slideWidth = wrap.clientWidth;
    if (!slideWidth) return;
    const slideCount = wrap.querySelectorAll('.acci-product-card__media').length;
    const index = Math.min(Math.max(Math.round(wrap.scrollLeft / slideWidth), 0), Math.max(slideCount - 1, 0));
    pagination.style.setProperty('--active-slide', String(index));
  };

  const scheduleCardPaginationUpdate = (wrap) => {
    if (!wrap || wrap.dataset.paginationPending) return;
    wrap.dataset.paginationPending = 'true';
    requestAnimationFrame(() => {
      delete wrap.dataset.paginationPending;
      updateCardPagination(wrap);
    });
  };

  const getCardVideo = (card) => card?.querySelector('video.acci-product-card__media--video') || null;

  const playCardVideo = (card) => {
    if (!card || card.dataset.hasVideo !== 'true') return;
    const video = getCardVideo(card);
    if (!video) return;
    video.muted = true;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  };

  const pauseCardVideo = (card) => {
    const video = getCardVideo(card);
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  };

  // On mobile the video is one of the swipe-gallery slides, so it plays inline
  // whenever it is the slide in view instead of waiting on a play button.
  const cardsInViewport = new WeakSet();

  const isVideoSlideInView = (card) => {
    const wrap = card.querySelector('.acci-product-card__media-wrap');
    const video = getCardVideo(card);
    if (!wrap || !video) return false;
    const slideWidth = wrap.clientWidth;
    if (!slideWidth) return false;
    const slides = Array.from(wrap.querySelectorAll('.acci-product-card__media'));
    return slides[Math.round(wrap.scrollLeft / slideWidth)] === video;
  };

  const syncMobileCardVideo = (card) => {
    if (!card || window.innerWidth > 768) return;
    const video = getCardVideo(card);
    if (!video) return;
    const shouldPlay = cardsInViewport.has(card) && isVideoSlideInView(card);
    if (shouldPlay === !video.paused) return;
    if (shouldPlay) {
      playCardVideo(card);
    } else {
      pauseCardVideo(card);
    }
  };

  const cardViewportObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                cardsInViewport.add(entry.target);
              } else {
                cardsInViewport.delete(entry.target);
              }
              syncMobileCardVideo(entry.target);
            });
          },
          { threshold: 0.25 }
        )
      : null;

  const observeCardsForMobileVideo = (root) => {
    root.querySelectorAll('.acci-product-card[data-has-video="true"]').forEach((card) => {
      if (cardViewportObserver) {
        cardViewportObserver.observe(card);
      } else {
        cardsInViewport.add(card);
        syncMobileCardVideo(card);
      }
    });
  };

  // Desktop hover readiness: once a card with a video nears the viewport, prime its
  // <video> (poster + source + a light preload) so the first hover starts near-instantly.
  // Off-screen cards are never touched (true lazy), and iframes/extra gallery images are
  // left to hover/swipe hydration. Flip VIDEO_PRELOAD_MODE to 'auto' to fully buffer
  // in-view videos (instant playback on hover, but downloads whole files while scrolling).
  const VIDEO_PRELOAD_MODE = 'metadata';
  const VIDEO_PRELOAD_MARGIN = '600px 0px';

  const primeCardVideo = (card) => {
    const video = getCardVideo(card);
    if (!video || video.dataset.primed) return;
    video.dataset.primed = 'true';
    if (video.dataset.poster) {
      video.setAttribute('poster', video.dataset.poster);
      video.removeAttribute('data-poster');
    }
    let sourceChanged = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
      sourceChanged = true;
    });
    video.preload = VIDEO_PRELOAD_MODE;
    if (sourceChanged) video.load();
  };

  const videoPreloadObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              // Hover is a desktop interaction; mobile keeps its swipe-to-hydrate behaviour.
              if (window.innerWidth <= 768) return;
              primeCardVideo(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: VIDEO_PRELOAD_MARGIN }
        )
      : null;

  const observeVideosForPreload = (root) => {
    if (!videoPreloadObserver) return;
    root.querySelectorAll('.acci-product-card[data-has-video="true"]').forEach((card) => {
      videoPreloadObserver.observe(card);
    });
  };

  observeCardsForMobileVideo(document);

  // Defer priming until the page has loaded so it never competes with LCP images.
  if (document.readyState === 'complete') {
    observeVideosForPreload(document);
  } else {
    window.addEventListener('load', () => observeVideosForPreload(document), { once: true });
  }

  document.addEventListener('mouseover', (event) => {
    if (window.innerWidth <= 768) return;
    const card = event.target.closest('.acci-product-card');
    if (!card) return;
    hydrateCardGallery(card.querySelector('.acci-product-card__media-wrap'));
    playCardVideo(card);
  });

  document.addEventListener('mouseout', (event) => {
    if (window.innerWidth <= 768) return;
    const card = event.target.closest('.acci-product-card');
    if (!card) return;
    if (event.relatedTarget && card.contains(event.relatedTarget)) return;
    pauseCardVideo(card);
  });

  document.addEventListener('focusin', (event) => {
    if (window.innerWidth <= 768) return;
    const card = event.target.closest('.acci-product-card');
    if (!card) return;
    hydrateCardGallery(card.querySelector('.acci-product-card__media-wrap'));
    playCardVideo(card);
  });

  document.addEventListener('focusout', (event) => {
    const card = event.target.closest('.acci-product-card');
    if (card && !card.contains(event.relatedTarget)) pauseCardVideo(card);
  });

  // Tracks whether the current touch started on a card gallery and which axis
  // it locked to, so Lenis (syncTouch preventDefaults touchmove) can let
  // horizontal gallery swipes scroll natively.
  const galleryGesture = { active: false, startX: 0, startY: 0, horizontal: null };

  document.addEventListener(
    'touchstart',
    (event) => {
      const wrap = event.target instanceof Element ? event.target.closest('.acci-product-card__media-wrap') : null;
      galleryGesture.active = Boolean(wrap);
      galleryGesture.horizontal = null;
      if (!wrap) return;
      hydrateCardGallery(wrap);
      galleryGesture.startX = event.touches[0].clientX;
      galleryGesture.startY = event.touches[0].clientY;
    },
    { passive: true, capture: true }
  );

  const endGalleryGesture = () => {
    galleryGesture.active = false;
    galleryGesture.horizontal = null;
  };
  document.addEventListener('touchend', endGalleryGesture, { passive: true, capture: true });
  document.addEventListener('touchcancel', endGalleryGesture, { passive: true, capture: true });

  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.classList.contains('acci-product-card__media-wrap')) {
        hydrateCardGallery(target);
        scheduleCardPaginationUpdate(target);
        syncMobileCardVideo(target.closest('.acci-product-card'));
      }
    },
    { passive: true, capture: true }
  );

  window.addEventListener(
    'resize',
    () => {
      document.querySelectorAll('.acci-product-card__media-wrap').forEach(scheduleCardPaginationUpdate);
      document.querySelectorAll('.acci-product-card[data-has-video="true"]').forEach((card) => {
        if (window.innerWidth > 768) {
          if (!card.matches(':hover')) pauseCardVideo(card);
          return;
        }
        syncMobileCardVideo(card);
      });
    },
    { passive: true }
  );

  const isGalleryHorizontalSwipe = (event) => {
    if (!galleryGesture.active || !event || !String(event.type).includes('touch')) return false;
    if (galleryGesture.horizontal === null && event.touches && event.touches.length) {
      const dx = Math.abs(event.touches[0].clientX - galleryGesture.startX);
      const dy = Math.abs(event.touches[0].clientY - galleryGesture.startY);
      if (Math.max(dx, dy) > 6) galleryGesture.horizontal = dx > dy;
    }
    return galleryGesture.horizontal === true;
  };

  const resetShopScrollToTop = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch {
      window.scrollTo(0, 0);
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const lenis = window.__acciLenisInfinite;
    if (lenis && typeof lenis.scrollTo === 'function') {
      try {
        lenis.scrollTo(0, { immediate: true });
      } catch {
        /* Lenis API may differ by version */
      }
    }
  };

  if (sections.length) {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    resetShopScrollToTop();
    requestAnimationFrame(() => {
      resetShopScrollToTop();
      requestAnimationFrame(resetShopScrollToTop);
    });
    window.addEventListener('load', resetShopScrollToTop, { once: true });
    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted) {
          resetShopScrollToTop();
          requestAnimationFrame(resetShopScrollToTop);
        }
      },
      { passive: true }
    );
  }

  const markEntered = (section) => {
    if (!section || section.classList.contains('is-entered')) return;
    // Ensure the initial hidden state has painted before we flip to entered.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        section.classList.add('is-entered');
      });
    });
  };

  const loadLenis = (() => {
    let promise;

    return () => {
      if (window.Lenis) return Promise.resolve(window.Lenis);
      if (promise) return promise;

      promise = new Promise((resolve, reject) => {
        if (!document.querySelector('link[data-acci-lenis-css]')) {
          const css = document.createElement('link');
          css.rel = 'stylesheet';
          css.href = `https://unpkg.com/lenis@${LENIS_VERSION}/dist/lenis.css`;
          css.setAttribute('data-acci-lenis-css', 'true');
          document.head.appendChild(css);
        }

        const existingScript = document.querySelector('script[data-acci-lenis-script]');
        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(window.Lenis), { once: true });
          existingScript.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = `https://unpkg.com/lenis@${LENIS_VERSION}/dist/lenis.min.js`;
        script.defer = true;
        script.setAttribute('data-acci-lenis-script', 'true');
        script.onload = () => resolve(window.Lenis);
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return promise;
    };
  })();

  const ensureInfiniteLenis = async (grid) => {
    if (window.__acciLenisInfiniteInitialized) return;

    try {
      const Lenis = await loadLenis();
      if (!Lenis) return;

      const lenis = new Lenis({
        autoRaf: true,
        infinite: true,
        syncTouch: true,
        // Returning false makes Lenis ignore the event (no preventDefault),
        // so horizontal card-gallery swipes scroll the gallery natively.
        virtualScroll: (data) => !isGalleryHorizontalSwipe(data.event),
      });

      window.__acciLenisInfinite = lenis;
      window.__acciLenisInfiniteInitialized = true;

      queueMicrotask(() => {
        try {
          lenis.scrollTo(0, { immediate: true });
        } catch {
          /* ignore */
        }
      });

      const destroyLenis = () => {
        if (window.__acciLenisInfinite) {
          window.__acciLenisInfinite.destroy();
          window.__acciLenisInfinite = null;
          window.__acciLenisInfiniteInitialized = false;
        }
      };

      // Destroy when leaving the page so BFCache doesn't preserve an active Lenis instance
      window.addEventListener('pagehide', destroyLenis, { once: true });

      // If BFCache restores this page and Lenis somehow survived, destroy and re-init cleanly
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) destroyLenis();
      }, { once: true });

      // Ensure it destroys itself if the grid is removed from DOM
      // (useful for SPA transitions where window isn't reset but DOM changes)
      const cleanupInterval = setInterval(() => {
        if (!document.body.contains(grid) || !grid.closest('body')) {
          destroyLenis();
          clearInterval(cleanupInterval);
        }
      }, 500);

      // Custom infinite scroll dom duplication
      if (grid && !grid.dataset.cloned) {
        grid.dataset.cloned = "true";

        // Create a seamless loop structure for Lenis
        // We append a container with exactly 100vh height.
        // This increases the scrollHeight by exactly innerHeight, so limit = H_grid.
        // Inside this container, we place the absolute positioned clone at top: 0.
        // So when the viewport reaches limit (H_grid), it perfectly shows the clone.
        const cloneContainer = document.createElement('div');
        cloneContainer.style.position = 'relative';
        cloneContainer.style.height = '100vh';
        cloneContainer.style.overflow = 'hidden';

        // Insert container right after the grid
        grid.parentNode.insertBefore(cloneContainer, grid.nextSibling);

        // Clone the grid exactly
        const clone = grid.cloneNode(true);
        clone.removeAttribute('id');
        clone.classList.add('acci-shop-grid--clone');
        clone.dataset.isClone = "true";
        clone.style.position = 'absolute';
        clone.style.top = '0';
        clone.style.left = '0';
        clone.style.width = '100%';
        clone.style.height = 'max-content';
        // Hide clone from screen readers
        clone.setAttribute('aria-hidden', 'true');
        
        cloneContainer.appendChild(clone);
        observeCardsForMobileVideo(clone);
        observeVideosForPreload(clone);

        // Update container height on resize to perfectly match 100vh
        // Using innerHeight is safer than 100vh CSS unit on mobile devices
        const updateHeight = () => {
          cloneContainer.style.height = `${window.innerHeight}px`;
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
      }
    } catch (error) {
      console.error('[section-shop-grid] Failed to initialize Lenis:', error);
    }
  };

  // Global cleanup: if we are on a page without any loop-enabled grids, 
  // ensure any lingering Lenis instance is destroyed.
  const hasLoopingGrid = Array.from(sections).some(s => s.dataset.loopEnabled === 'true');
  if (!hasLoopingGrid && window.__acciLenisInfiniteInitialized && window.__acciLenisInfinite) {
    window.__acciLenisInfinite.destroy();
    window.__acciLenisInfinite = null;
    window.__acciLenisInfiniteInitialized = false;
  }

  sections.forEach((section) => {
    const titleNode = section.querySelector('.acci-shop__title');
    const grid = section.querySelector('.acci-shop-grid');
    const filterWrap = section.querySelector('.acci-shop__filter-wrap');
    const filterMenu = section.querySelector('.acci-shop__filter-menu');
    const loopEnabled = section.dataset.loopEnabled === 'true';

    const isMarket = section.classList.contains('acci-market') || section.dataset.collectionHandle === 'market';

    const syncFilterMenuWithShopDropdown = () => {
      if (isMarket) return; // Use the collection's tags directly instead of the main nav

      const shopDropdownItem = Array.from(
        document.querySelectorAll('.acci-nav__list > .acci-nav__item--has-dropdown')
      ).find((item) => {
        const trigger = item.querySelector('.acci-nav__link');
        return (trigger?.textContent || '').trim().toLowerCase() === 'shop';
      });

      const shopDropdownLinks = shopDropdownItem
        ? shopDropdownItem.querySelectorAll('.acci-nav__dropdown-link')
        : [];

      if (!shopDropdownLinks.length) return;

      const totalLinks = shopDropdownLinks.length;
      const menuMarkup = Array.from(shopDropdownLinks)
        .map((link, index) => {
          const href = link.getAttribute('href') || '#';
          const text = (link.textContent || '').trim();
          const linkUrl = new URL(href, window.location.origin);
          const isActive = linkUrl.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '');
          const activeClass = isActive ? ' underline-dots-static' : '';
          const ariaCurrent = isActive ? ' aria-current="page"' : '';

          if (!text) return '';

          const reverseIndex = Math.max(0, (totalLinks - 1) - index);
          return `<li style="--dropdown-index: ${index}; --dropdown-index-up: ${reverseIndex};"><a class="underline-animation${activeClass}" href="${href}"${ariaCurrent}>${text}</a></li>`;
        })
        .join('');

      if (menuMarkup) {
        filterMenu.innerHTML = menuMarkup;
      }
    };

    const syncGridRowHeight = () => {
      if (!grid) return;
      const referenceItem = grid.querySelector('.acci-shop-grid__item');
      if (!referenceItem) return;

      const referenceHeight = referenceItem.getBoundingClientRect().height;
      if (referenceHeight > 0) {
        grid.style.gridAutoRows = `${referenceHeight}px`;
      }
    };

    syncGridRowHeight();
    syncFilterMenuWithShopDropdown();
    markEntered(section);

    if (filterWrap) {
      const filterTrigger = section.querySelector('.acci-shop__filter-trigger');
      const closeFilterMenu = () => {
        filterWrap.classList.remove('is-open');
        if (filterTrigger) {
          filterTrigger.setAttribute('aria-expanded', 'false');
        }
      };
      
      const isDesktopHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const enableGlobalFade = () => document.body.classList.add('fade-global');
      const disableGlobalFadeWhenInactive = () => {
        if (document.getElementById('acci-cart-drawer')?.classList.contains('is-open')) return;
        if (!isDesktopHover()) {
          if (!document.querySelector('.acci-shop__filter-wrap.is-open, .lookbook-viewer__credits-wrap.is-open')) {
            document.body.classList.remove('fade-global');
          }
          return;
        }
        if (
          !document.querySelector(
            '.acci-nav .acci-nav__item:hover, .acci-nav .acci-nav__item:focus-within, .acci-shop__filter-wrap:hover, .acci-shop__filter-wrap:focus-within, .acci-shop__filter-wrap.is-open, .acci-header__right:hover, .acci-header__right:focus-within'
          )
        ) {
          document.body.classList.remove('fade-global');
        }
      };

      filterWrap.addEventListener('mouseenter', () => {
        if (isDesktopHover()) enableGlobalFade();
      });
      filterWrap.addEventListener('mouseleave', () => {
        if (isDesktopHover()) {
          if (filterWrap.contains(document.activeElement) && typeof document.activeElement?.blur === 'function') {
            document.activeElement.blur();
          }
          closeFilterMenu();
          disableGlobalFadeWhenInactive();
        }
      });
      filterWrap.addEventListener('focusin', () => {
        if (isDesktopHover()) enableGlobalFade();
      });
      filterWrap.addEventListener('focusout', () => {
        if (isDesktopHover()) requestAnimationFrame(disableGlobalFadeWhenInactive);
      });

      if (filterTrigger) {
        filterTrigger.setAttribute('aria-expanded', 'false');
        filterTrigger.addEventListener('click', (e) => {
          if (window.innerWidth <= 768) {
            e.preventDefault();
            const isOpen = filterWrap.classList.toggle('is-open');
            filterTrigger.setAttribute('aria-expanded', String(isOpen));
            if (isOpen) {
              enableGlobalFade();
            } else {
              document.body.classList.remove('fade-global');
              disableGlobalFadeWhenInactive();
            }
          }
        });
      }

      if (filterMenu) {
        filterMenu.addEventListener('click', (e) => {
          const clickedLink = e.target.closest('a');
          if (!clickedLink) return;

          closeFilterMenu();
          document.body.classList.remove('fade-global');
          disableGlobalFadeWhenInactive();
        });
      }

      document.addEventListener(
        'click',
        (e) => {
          if (window.innerWidth > 768) return;
          if (!filterWrap.classList.contains('is-open')) return;
          if (filterWrap.contains(e.target)) return;

          // Mobile outside tap should only close the filter, not activate underlying UI.
          e.preventDefault();
          e.stopPropagation();
          closeFilterMenu();
          document.body.classList.remove('fade-global');
          disableGlobalFadeWhenInactive();
        },
        true
      );
    }

    if (titleNode && grid) {
      const defaultTitle = (titleNode.dataset.defaultTitle || '').trim();
      titleNode.innerHTML = defaultTitle;
      const buildHoverTitle = (card) => {
        if (!card) return defaultTitle;

        const baseTitle = (card.dataset.productBaseTitle || card.dataset.productTitle || '').trim();
        const colourName = (card.dataset.productColour || '').trim();
        const vendorName = (card.dataset.productVendor || '').trim();
        const hasMultipleColours = card.dataset.hasMultipleColours === 'true';

        if (!baseTitle) return defaultTitle;

        const isMarket = section.classList.contains('acci-market') || section.dataset.collectionHandle === 'market';

        if (isMarket) {
          if (!vendorName) return baseTitle;
          return `<span class="acci-shop__title-vendor">${vendorName}</span><br><span>${baseTitle}</span>`;
        }

        if (!hasMultipleColours || !colourName) return baseTitle;

        return `<span>${baseTitle}</span><br><span class="acci-shop__title-colour">${colourName}</span>`;
      };

      section.addEventListener('mouseover', (event) => {
        if (window.innerWidth <= 768) return;
        const card = event.target.closest('.acci-product-card');
        if (!card) {
          titleNode.innerHTML = defaultTitle;
          return;
        }
        titleNode.innerHTML = buildHoverTitle(card);
      });

      section.addEventListener('focusin', (event) => {
        if (window.innerWidth <= 768) return;
        const card = event.target.closest('.acci-product-card');
        if (!card) {
          titleNode.innerHTML = defaultTitle;
          return;
        }
        titleNode.innerHTML = buildHoverTitle(card);
      });

      section.addEventListener('mouseleave', () => {
        if (window.innerWidth <= 768) return;
        titleNode.innerHTML = defaultTitle;
      });

      section.addEventListener('focusout', () => {
        if (window.innerWidth <= 768) return;
        if (section.contains(document.activeElement)) return;
        titleNode.innerHTML = defaultTitle;
      });

      let mobileActiveCard = null;
      let mobileMeasureRaf = null;
      const MOBILE_BREAKPOINT = 768;

      const updateMobileActiveCard = (card) => {
        if (mobileActiveCard === card) return;
        
        if (mobileActiveCard) {
          mobileActiveCard.classList.remove('is-active');
        }
        
        mobileActiveCard = card;
        
        if (mobileActiveCard) {
          mobileActiveCard.classList.add('is-active');
          grid.classList.add('acci-shop-grid--mobile-active');
          titleNode.innerHTML = buildHoverTitle(mobileActiveCard);
        } else {
          grid.classList.remove('acci-shop-grid--mobile-active');
          titleNode.innerHTML = defaultTitle;
        }
      };

      const getMobileCandidateCards = () =>
        Array.from(
          section.querySelectorAll(
            '.acci-shop-grid > .acci-shop-grid__item:not(.acci-shop-grid__item--filler) .acci-product-card'
          )
        );

      const findCenteredMobileCard = () => {
        const cards = getMobileCandidateCards();
        if (!cards.length) return null;

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        if (!viewportHeight) return null;
        const viewportCenter = viewportHeight / 2;

        let bestContainingCard = null;
        let bestContainingDistance = Infinity;
        let bestVisibleCard = null;
        let bestVisibleDistance = Infinity;
        let bestVisibleAmount = 0;

        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const visibleTop = Math.max(rect.top, 0);
          const visibleBottom = Math.min(rect.bottom, viewportHeight);
          const visibleAmount = visibleBottom - visibleTop;

          if (visibleAmount <= 0) return;

          const cardCenter = rect.top + rect.height / 2;
          const centerDistance = Math.abs(cardCenter - viewportCenter);
          const containsViewportCenter = rect.top <= viewportCenter && rect.bottom >= viewportCenter;

          if (containsViewportCenter && centerDistance < bestContainingDistance) {
            bestContainingDistance = centerDistance;
            bestContainingCard = card;
            return;
          }

          if (
            centerDistance < bestVisibleDistance ||
            (centerDistance === bestVisibleDistance && visibleAmount > bestVisibleAmount)
          ) {
            bestVisibleDistance = centerDistance;
            bestVisibleAmount = visibleAmount;
            bestVisibleCard = card;
          }
        });

        return bestContainingCard || bestVisibleCard || null;
      };

      const measureMobileActiveCard = () => {
        mobileMeasureRaf = null;

        if (window.innerWidth > MOBILE_BREAKPOINT) {
          if (mobileActiveCard) updateMobileActiveCard(null);
          return;
        }

        updateMobileActiveCard(findCenteredMobileCard());
      };

      const scheduleMobileActiveMeasure = () => {
        if (mobileMeasureRaf !== null) return;
        mobileMeasureRaf = requestAnimationFrame(measureMobileActiveCard);
      };

      scheduleMobileActiveMeasure();

      window.addEventListener('scroll', scheduleMobileActiveMeasure, { passive: true });
      window.addEventListener('resize', scheduleMobileActiveMeasure, { passive: true });
      window.addEventListener('orientationchange', scheduleMobileActiveMeasure, { passive: true });

      window.addEventListener('resize', () => {
        if (window.innerWidth > MOBILE_BREAKPOINT && mobileActiveCard) {
          updateMobileActiveCard(null);
          titleNode.innerHTML = defaultTitle;
        }
      });
    }

    window.addEventListener('resize', syncGridRowHeight);

    if (!loopEnabled) return;
    ensureInfiniteLenis(grid);
  });
})();
